import { useQuery } from '@tanstack/react-query';
import { toError } from 'shared/util/toError';

import { fetchPolicy, fetchQuestionnaire, fetchQuote } from '../api';

const STALE_TIME = 1000 * 60 * 30;

export const useFetchPolicyAndEntities = ({
  policyId,
}: {
  policyId: string;
}) => {
  const {
    data: policy,
    isError: isPolicyFetchError,
    error: policyFetchError,
    isInitialLoading: isInitialLoadingPolicy,
  } = useQuery(['policyDetails', policyId], () => fetchPolicy(policyId), {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
  });

  const questionnaireId = policy?.attributes.questionnaireId;
  const quoteId = policy?.attributes.quoteId;

  const {
    data: policyQuestionnaire,
    isError: isQuestionnaireFetchError,
    error: questionnaireFetchError,
    isInitialLoading: isInitialLoadingQuestionnaire,
  } = useQuery(
    ['questionnaire', questionnaireId],
    () => fetchQuestionnaire(questionnaireId || ''),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: !!questionnaireId,
      staleTime: STALE_TIME,
    }
  );

  const {
    data: policyQuote,
    isError: isQuoteFetchError,
    error: quoteFetchError,
    isInitialLoading: isInitialLoadingQuote,
  } = useQuery(['quote', quoteId], () => fetchQuote(quoteId || ''), {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    enabled: !!quoteId,
    staleTime: STALE_TIME,
  });

  const unknownError =
    policyFetchError || questionnaireFetchError || quoteFetchError;

  return {
    policy,
    policyQuestionnaire,
    policyQuote,
    isError:
      isPolicyFetchError || isQuestionnaireFetchError || isQuoteFetchError,
    error: unknownError ? toError(unknownError) : undefined,
    isInitialLoading:
      isInitialLoadingPolicy ||
      isInitialLoadingQuestionnaire ||
      isInitialLoadingQuote,
  };
};
